import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import moment from "moment";
import { Observable } from "rxjs";
import { dateToUtcIsoString } from "src/app/common/datetime_utility";
import { JsonHalCollection } from "src/app/models/api/jsonhal.model";
import { PlaybookExecution } from "src/app/models/api/playbooks/playbookExecution.model";
import { ISelectedFilters } from "src/app/models/filters/selectedFilters.interface";
import { SortColumn } from "src/app/models/sortColumn.model";
import { LoggerService } from "../../infra/logger/logger.service";
import { HttpService } from "../http/http.service";

@Injectable({
    providedIn: "root",
})
export class PlaybooksExecutionsHttpServiceService {
    public readonly defaultPageSize: number = 50;
    private readonly _baseUrl: string = "playbooks/executions";

    constructor(
        private _httpService: HttpService,
        private _logger: LoggerService,
    ) {}

    /**
     * Get default violation rules
     * @returns A collection of default violation rules
     */
    public getExecutionsFirstPage(filterOptions?: ISelectedFilters, sort?: SortColumn[]): Observable<JsonHalCollection<PlaybookExecution>> {
        const url = `${this._baseUrl}`;
        const httpParams = this._createQueryParams(filterOptions, sort, true);

        return this._httpService.get<JsonHalCollection<PlaybookExecution>>(url, undefined, httpParams);
    }

    /**
     * @public Get vulnerabilities by url, received from the first call of the api.
     * @returns an Observable{VulnerabilitiesResponse} with the vulnerabilities returned from server
     */
    public getExecutionsByContinuationToken(continuationToken: string): Observable<JsonHalCollection<PlaybookExecution>> {
        const url = `${this._baseUrl}/pages`;
        const httpParams = new HttpParams().append("continuationToken", continuationToken);

        return this._httpService.get<JsonHalCollection<PlaybookExecution>>(url, undefined, httpParams);
    }

    private _createQueryParams(filterOptions?: ISelectedFilters, sortData?: SortColumn[], shouldLimitResultsAmount = false): HttpParams {
        let httpParams = new HttpParams();

        if (shouldLimitResultsAmount) {
            httpParams = httpParams.append("pageSize", this.defaultPageSize);
        }

        if (filterOptions) {
            httpParams = this._appendFilterQueryParams(httpParams, filterOptions);
        }

        if (sortData) {
            httpParams = this._appendSortToHttpParams(httpParams, sortData);
        }
        return httpParams;
    }

    private _appendFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
        if (filterOptions.playbook && filterOptions.playbook.length > 0) {
            httpParams = httpParams.append("playbookIds", filterOptions.playbook.join(","));
        }

        if (filterOptions.executionStatus && filterOptions.executionStatus.length > 0) {
            httpParams = httpParams.append("executionStatus", filterOptions.executionStatus.join(","));
        }

        if (filterOptions.lastExecutionSince) {
            httpParams = httpParams.append("endTimeSince", dateToUtcIsoString(moment(filterOptions.lastExecutionSince).startOf("day").toDate()));
        }

        if (filterOptions.lastExecutionUntil) {
            httpParams = httpParams.append("endTimeUntil", dateToUtcIsoString(moment(filterOptions.lastExecutionUntil).endOf("day").toDate()));
        }

        if (filterOptions.search) {
            httpParams = httpParams.append("textSearch", filterOptions.search);
        }

        return httpParams;
    }

    private _appendSortToHttpParams(httpParams: HttpParams, sortData: SortColumn[]): HttpParams {
        const sortArray: string[] = [];

        sortData.forEach((sort) => {
            const sortFieldString: string | undefined = sort.column.sortKey;
            const ascOrDescString = sort.isDesc ? "desc" : "asc";
            if (sortFieldString) {
                sortArray.push(`${sortFieldString}(${ascOrDescString})`);
            } else {
                this._logger.error(`Sort violation column ${sort.column.propertyKey} was not implemented`);
            }
        });
        return httpParams.append("sortBy", `[${sortArray}]`);
    }
}
